import { useEffect } from "react";
import { useQuery } from "react-query";
import { getAgeFromMetadata } from "lib/client/normative";
import { fetchStudyMetadata } from "lib/client/dicomweb";
import safeNaturalizeDataset from "lib/cornerstone/helpers/safeNaturalizeDataset";
import queryClient from "lib/client/main";
import { groupBy } from "shared/utils/recursive";
import useViewerParams from "./useViewerParams";
import { SERIES_INSTANCE_UID } from "lib/types/dicomTags";

function useStudyMetadata(study_id: string) {
  const {patient_id} = useViewerParams()
  const { data, isLoadingError, isLoading, ...studyQuery } = useQuery(
    ["studies", study_id, "metadata", patient_id],
    ({ queryKey }) => fetchStudyMetadata(queryKey[1]!, queryKey[3]!),
  )

  useEffect(() => {
    if (data?.length) { // Pre cache the need metadata series to prevent call to dicom-web/studies//series//metadata 
      const datasetsPerSeries = groupBy(data, (d) => String(d?.[SERIES_INSTANCE_UID].Value))
      Object.entries(datasetsPerSeries).forEach(([serieUID, datasets]) => {
        queryClient.setQueryData(["studies", study_id, "series", serieUID, "metadata"], datasets)
      })
    }
  }, [data, study_id])

  const naturalizedDatasets : ReturnType<typeof safeNaturalizeDataset>[] = (data || []).map(safeNaturalizeDataset);
  const isDone = !isLoadingError;

  const firstDataset = naturalizedDatasets?.[0];
  const age = getAgeFromMetadata(firstDataset);
  const sex = firstDataset?.PatientSex;
  const PatientID = firstDataset?.PatientID;
  const PatientName = firstDataset?.PatientName?.[0]?.Alphabetic;

  const qynapseMetadata = naturalizedDatasets?.find((dataset) => dataset?.Manufacturer === "Qynapse");
  const version = qynapseMetadata?.SoftwareVersions;
  const uniqueDeviceIdentifier = qynapseMetadata?.UDISequence?.[0]?.UniqueDeviceIdentifier;
  const beta = "PRELIMINARY" === qynapseMetadata?.PreliminaryFlag;
  const certifications = qynapseMetadata?.ApprovalSequence?.map((tag) => tag.CodeValue);

  return {
    ...studyQuery,
    isDone,
    age,
    sex,
    certifications,
    PatientID,
    PatientName,
    version,
    naturalizedDatasets,
    uniqueDeviceIdentifier,
    beta,
    isLoading,
  };
}

export default useStudyMetadata;
